import moment from 'moment'
import { getByteLen } from '@/common/js/utils'
import { PLATFORM } from '@/common/js/constants.js'
import {
  APP_PROMOTION_TYPE,
  BID_VALUE_TYPE,
  BUDGET_MODE,
  BID_TYPE,
  AMEME_TYPE,
  DELIVERY_MODE,
  SCHEDULE_TIME_TYPE,
  SCHEDULE_TYPE,
  BLUE_FLOW_PACKAGE_SETTING_SWITCH,
  AUTO_EXTEND_TRAFFIC_SWITCH,
  MATERIAL_STYLE
} from '../consts/index.js'

import { useOptimizeGoalRelative } from '../hooks/useOptimizeGoal.js'
import { useProjectParamStates } from '../hooks/useCommonComputeds.js'

const twoDigit = /^[0-9]+([.]{1}[0-9]{1,2})?$/
const fourDigit = /^[0-9]+([.]{1}[0-9]{1,4})?$/
const MINIMUM_BUDGET = 300
const MAXIMUM_BUDGET = 999999.99

const MINIMUM_BID = 0.01
const MAXIMUM_BID = 10000

const MINIMUM_ROI = 0.0001
const MAXIMUM_ROI = 5

const MINIMUM_PLAN_BID = 0.1
const MAXIMUM_PLAN_BID = 10000

/**
 * @description: 项目信息验证规则
 * @return {*}
 */
export function getProjectFormRules({ standardEdit = false } = {}) {
  const projectForm = this
  const { projectBidVisible, adDeepBidVisible, isFirstRoiGoal } = useOptimizeGoalRelative(projectForm)
  const { isManual } = useProjectParamStates(projectForm)

  return {
    // 批量复制、修改流程需要校验
    plan_name: [
      {
        validator(rule, value, callback) {
          if (typeof value === 'undefined') return callback()
          if (Math.ceil(getByteLen(value)) > 90) return callback(new Error('广告名称长度超出限制'))
          callback()
        }
      }
    ],

    project_name: [
      { required: true, message: '请输入项目名称' },
      {
        validator(rule, value, callback) {
          if (Math.ceil(getByteLen(value)) > 90) return callback(new Error('项目名称长度超出限制'))
          callback()
        }
      }
    ],
    'game.id': [
      // { required: true, message: '请选择游戏' },
      {
        validator(rule, value, callback) {
          const { game } = projectForm
          if (!game?.id) return callback(new Error('请选择游戏'))
          callback()
        }
      }
    ],

    micro_app_instance_id: [
      {
        required: true,
        validator(rule, value, callback) {
          const { ch_platform_id } = projectForm
          if (!value && PLATFORM.MINI_PROGRAM === ch_platform_id) return callback(new Error('请填写小程序资产id'))
          callback()
        }
      }
    ],

    download_url: [
      {
        validator(rule, value, callback) {
          if (PLATFORM.IOS === projectForm.ch_platform_id) {
            if (!value.trim() && projectForm.app_promotion_type === APP_PROMOTION_TYPE.DOWNLOAD) {
              return callback(new Error('请输入下载链接'))
            }
          }

          callback()
        }
      }
    ],
    subscribe_url: [
      {
        required: true,
        validator(rule, value, callback) {
          if (!value.trim() && projectForm.app_promotion_type === APP_PROMOTION_TYPE.RESERVE) {
            return callback(new Error('请输入预约链接'))
          }
          callback()
        }
      }
    ],
    toutiaoAcid: [
      {
        required: true,
        validator(rule, value, callback) {
          const { ch_platform_id, app_promotion_type, game } = projectForm

          if (PLATFORM.ANDROID === ch_platform_id && app_promotion_type === APP_PROMOTION_TYPE.DOWNLOAD) {
            if (!game.toutiaoAcid) return callback(new Error('该应用未配置头条分包信息'))
          }
          callback()
        }
      }
    ],
    star_task: [
      {
        validator(rule, value, callback) {
          const { star_auto_material_addition_switch } = projectForm
          const { star_task_id } = value || {}

          if (star_task_id && star_auto_material_addition_switch === 'ON') {
            if (isManual.value) {
              return callback(new Error('星广联投任务开启自动优选素材时只能选择自动投放'))
            }
          }
          callback()
        }
      }
    ],
    blue_flow_package: [
      {
        validator(rule, value, callback) {
          const { blue_flow_package } = projectForm
          const { blue_flow_package_setting, blue_flow_package_id } = blue_flow_package
          if (blue_flow_package_setting === BLUE_FLOW_PACKAGE_SETTING_SWITCH.ON) {
            if (!blue_flow_package_id) {
              return callback(new Error('请选择蓝海流量包'))
            }
          }
          callback()
        }
      }
    ],
    search_bid_ratio: [
      {
        validator(rule, value, callback) {
          const { delivery_mode, keywordSwitch, search_bid_ratio } = projectForm

          if (delivery_mode === DELIVERY_MODE.MANUAL && keywordSwitch) {
            if (search_bid_ratio) {
              if (isNaN(search_bid_ratio)) return callback(new Error('请输入数字'))
              if (Number(search_bid_ratio) >= 1 && Number(search_bid_ratio) <= 2) {
                if (!twoDigit.test(search_bid_ratio)) {
                  return callback(new Error('出价系数值小数点后最多两位'))
                }
              } else {
                return callback(new Error('出价系数取值范围1.00 ~ 2.00'))
              }
            }
          }
          callback()
        }
      }
    ],
    'delivery_setting.date_range': [
      {
        required: true,
        validator(rule, value, callback) {
          const errors = []
          const { date_range, schedule_type } = projectForm.delivery_setting
          if (schedule_type === SCHEDULE_TYPE.START_END) {
            if (!date_range) errors.push('请选择投放日期')

            if (!date_range?.length || date_range?.length !== 2) {
              errors.push('请选择投放日期')
            }

            const [beginDate, endDate] = date_range
            if (!endDate) errors.push('请选择投放结束日期')
            if (!standardEdit && moment(`${beginDate}`).isBefore(moment().format('YYYY-MM-DD'))) {
              errors.push('投放日期不能小于今天')
            }
            if (standardEdit && endDate && moment(`${endDate}`).isBefore(moment().format('YYYY-MM-DD'))) {
              errors.push('投放结束日期不能小于今天')
            }

            if (errors.length) {
              callback(new Error(errors[0]))
            }
          }

          callback()
        }
      }
    ],
    'delivery_setting.schedule_time': [
      {
        required: true,
        validator(rule, value, callback) {
          const { schedule_time, schedule_time_type } = projectForm.delivery_setting
          if (schedule_time_type === SCHEDULE_TIME_TYPE.RANGE) {
            if (!schedule_time) {
              return callback(new Error('请选择投放时段'))
            }
          }

          callback()
        }
      }
    ],
    'delivery_setting.budget_mode': [
      {
        required: true,
        validator(rule, value, callback) {
          const { budget_mode, budget } = projectForm.delivery_setting
          if (budget_mode === BUDGET_MODE.DAY) {
            if (budget === '') return callback(new Error('请填写预算'))
            if (isNaN(Number(budget))) return callback(new Error('请输入数字'))
            if (budget < MINIMUM_BUDGET) return callback(new Error(`预算不能低于${MINIMUM_BUDGET}元`))
            if (budget > MAXIMUM_BUDGET) return callback(new Error(`预算不能高于${MAXIMUM_BUDGET}元`))
            if (!twoDigit.test(budget)) return callback(new Error('最多保留2位小数'))
          }

          callback()
        }
      }
    ],
    'delivery_setting.cpa_bid': [
      {
        required: true,
        validator(rule, value, callback) {
          let { budget, bid_type, cpa_bid, bid_value_type, bid_num_range } = projectForm.delivery_setting
          budget = Number(budget)
          value = Number(cpa_bid)

          if (projectBidVisible.value) {
            // 最大转化不需要校验
            if (bid_type === BID_TYPE.NO_BID) return callback()

            if (bid_value_type === BID_VALUE_TYPE.RANGE) {
              const [bid_start, bid_end] = bid_num_range.map(Number)
              if (isNaN(bid_start)) return callback(new Error('请输入数字'))
              if (isNaN(bid_end)) return callback(new Error('请输入数字'))
              if (bid_end <= bid_start) return callback(new Error('最高出价须大于最低出价'))
              if (bid_start < MINIMUM_BID) return callback(new Error(`出价不能低于${MINIMUM_BID}元`))
              if (bid_end > MAXIMUM_BID) return callback(new Error(`出价不能高于${MAXIMUM_BID}元`))
              if (bid_end > budget) return callback(new Error('出价须小于预算'))
              if (!twoDigit.test(bid_start)) return callback(new Error('最多保留2位小数'))
              if (!twoDigit.test(bid_end)) return callback(new Error('最多保留2位小数'))
            } else {
              if (isNaN(value)) {
                return callback(new Error('请输入数字'))
              }
              if (value < MINIMUM_BID) return callback(new Error(`出价不能低于${MINIMUM_BID}元`))
              if (value > MAXIMUM_BID) return callback(new Error(`出价不能高于${MAXIMUM_BID}元`))
              if (value > budget) return callback(new Error('出价须小于预算'))
              if (!twoDigit.test(value)) return callback(new Error('最多保留2位小数'))
            }
          }

          callback()
        }
      }
    ],
    'delivery_setting.first_roi_goal': [
      {
        required: true,
        validator(rule, value, callback) {
          const { first_roi_goal } = projectForm.delivery_setting
          value = Number(first_roi_goal)
          if (projectBidVisible.value && isFirstRoiGoal.value) {
            if (isNaN(value)) {
              return callback(new Error('请输入数字'))
            }

            if (value < MINIMUM_ROI) return callback(new Error(`首日ROI不能低于${MINIMUM_ROI}`))
            if (value > MAXIMUM_ROI) return callback(new Error(`首日ROI不能高于${MAXIMUM_ROI}`))
            if (!fourDigit.test(value)) return callback(new Error('最多保留4位小数'))
          }
          callback()
        }
      }
    ],
    'delivery_setting.roi_goal': [
      {
        required: true,
        validator(rule, value, callback) {
          const { bid_type, roi_goal, roi_bid_value_type, roi_bid_num_range } = projectForm.delivery_setting

          value = Number(roi_goal)
          if (adDeepBidVisible.value && projectBidVisible.value) {
            // 最大转化不需要校验
            if (bid_type === BID_TYPE.NO_BID) return callback()

            if (roi_bid_value_type === BID_VALUE_TYPE.RANGE) {
              const [bid_roi_start, bid_roi_end] = roi_bid_num_range.map(Number)
              if (isNaN(bid_roi_start)) return callback(new Error('请输入数字'))
              if (isNaN(bid_roi_end)) return callback(new Error('请输入数字'))
              if (bid_roi_end <= bid_roi_start) return callback(new Error('roi最高出价不能大于等于最低出价'))
              if (bid_roi_start < MINIMUM_ROI) return callback(new Error(`roi不能低于${MINIMUM_ROI}`))
              if (bid_roi_end > MAXIMUM_ROI) return callback(new Error(`roi不能高于${MAXIMUM_ROI}`))
              if (!fourDigit.test(bid_roi_start)) return callback(new Error('最多保留4位小数'))
              if (!fourDigit.test(bid_roi_end)) return callback(new Error('最多保留4位小数'))
            } else {
              if (value < MINIMUM_ROI) return callback(new Error(`roi不能低于${MINIMUM_ROI}`))
              if (value > MAXIMUM_ROI) return callback(new Error(`roi不能高于${MAXIMUM_ROI}`))
              if (!fourDigit.test(value)) return callback(new Error('最多保留4位小数'))
            }
          }
          callback()
        }
      }
    ]
  }
}

/**
 * @description: 广告信息表单验证规则
 * @return {*}
 */
export function getPlanFormRules() {
  const { planForm, projectForm } = this
  const { isSearch, isDuration, isBlueFlowEnable, adBidVisible, adDeepBidVisible, adBudgetVisible } = useOptimizeGoalRelative(projectForm)
  const isLiveMaterial = planForm.materials_type === MATERIAL_STYLE.LIVE_MATERIALS

  return {
    aweme_id: [
      {
        validator(rule, value, callback) {
          const { awemeIdentity, aweme_id } = planForm
          if (AMEME_TYPE.DOUYIN === awemeIdentity) {
            if (!aweme_id) callback(new Error('请选择抖音号'))
          }
          callback()
        }
      }
    ],
    source: [
      {
        required: true,
        validator(rule, value, callback) {
          if (isLiveMaterial) return callback()
          const { ch_platform_id } = projectForm
          // 小程序推广必填
          if (!value && PLATFORM.MINI_PROGRAM === ch_platform_id) return callback(new Error('请输入来源'))

          // 其他推广如果填了再验证字数限制
          if (value) {
            const len = getByteLen(value)
            if (len > 20 || len < 4) {
              return callback(new Error('广告来源字数限制2~10，英文字符限制4~20'))
            }
          }
          callback()
        }
      }
    ],
    app_name: [
      {
        validator(rule, value, callback) {
          if (isLiveMaterial) return callback()
          if (isDuration.value) return callback()
          const { app_name } = planForm
          if (!app_name) callback(new Error('请输入产品名称'))
          callback()
        }
      }
    ],
    app_image: [
      {
        required: true,
        validator(rule, value, callback) {
          if (isLiveMaterial) return callback()
          if (isDuration.value) return callback()

          const { app_image } = planForm
          if (!app_image?.id) return callback(new Error('请选择产品主图'))
          const { width, height, size } = app_image
          if (width !== 108 || height !== 108) {
            return callback(new Error(`产品主图尺寸为108x108，当前尺寸为${width}*${height}`))
          }

          if (size / 1024 > 1024) {
            return callback(new Error('产品主图大小不能超过1M'))
          }

          callback()
        }
      }
    ],
    blue_flow_keywords: [
      {
        required: true,
        validator(rule, value, callback) {
          const { blue_flow_keywords } = planForm
          if (isBlueFlowEnable.value && !blue_flow_keywords) return callback(new Error('请选择蓝海流量关键词'))

          callback()
        }
      }
    ],
    // auto_extend_traffic: [
    //   {
    //     validator(rule, value, callback) {
    //       const { keywords, auto_extend_traffic } = planForm
    //       if (isSearch.value) {
    //         if (!keywords?.length && auto_extend_traffic === AUTO_EXTEND_TRAFFIC_SWITCH.OFF) {
    //           return callback(new Error('蓝海流量关键词和智能拓流二者必须开启一个'))
    //         }
    //       }
    //       callback()
    //     }
    //   }
    // ],

    // 落地页的校验规则只会在批量复制、修改流程生效，当新建流程时typeof value === 'undefined'
    web_url_material_list: [
      {
        validator(rule, value, callback) {
          if (isLiveMaterial) return callback()
          // 如果value = undefined，不进行校验
          if (typeof value === 'undefined') return callback()
          if (value.some(url => !url.trim())) {
            return callback(new Error('请填写落地页链接'))
          }

          callback()
        }
      }
    ],
    // 文案的校验规则只会在批量复制、修改流程生效，当新建流程时typeof value === 'undefined'
    title_material_list: [
      {
        required: true,
        validator(rule, value, callback) {
          if (isLiveMaterial) return callback()

          // 如果value = undefined，不进行校验
          if (typeof value === 'undefined') return callback()
          if (value.some(text => !text.title.trim())) {
            return callback(new Error('请填写创意标题'))
          }

          callback()
        }
      }
    ],
    text_abstract_list: [
      {
        validator(rule, value, callback) {
          if (isLiveMaterial) return callback()

          const { text_abstract_list } = planForm
          if (text_abstract_list.length > 10) return callback(new Error('最多添加10个文本摘要'))

          for (let i = 0; i < text_abstract_list.length; i++) {
            const item = text_abstract_list[i]

            // getByteLen计算的是一个中文占两个字符，一个英文占一个字符，所以除以二
            const len = getByteLen(item) / 2
            if (len > 45 || len < 25) {
              return callback(new Error(`第${i + 1}个摘要${len}个字符，最少25个字符，最多45个字符，一个中文占一个字符，两个英文占一个字符`))
            }
          }
          callback()
        }
      }
    ],
    selling_points_list: [
      {
        required: true,
        validator(rule, value, callback) {
          if (isLiveMaterial) return callback()
          if (isDuration.value) return callback()

          const { selling_points_list } = planForm
          if (!selling_points_list?.length) return callback(new Error('请添加产品卖点'))
          if (selling_points_list.length > 10) return callback(new Error('最多添加10个产品卖点'))

          for (let i = 0; i < selling_points_list.length; i++) {
            const item = selling_points_list[i]
            const len = getByteLen(item)
            if (len > 18 || len < 12) {
              return callback(new Error(`第${i + 1}个标签${len}个字符，最少12个字符，最多18个字符，一个中文占两个字符`))
            }
          }

          callback()
        }
      }
    ],
    call_to_action_buttons_list: [
      {
        required: true,
        validator(rule, value, callback) {
          if (isLiveMaterial) return callback()
          if (isDuration.value) return callback()
          const { call_to_action_buttons_list } = planForm
          if (!call_to_action_buttons_list?.length) return callback(new Error('请添加行动号召'))
          if (call_to_action_buttons_list.length > 10) return callback(new Error('最多添加10个行动号召'))

          for (let i = 0; i < call_to_action_buttons_list.length; i++) {
            const item = call_to_action_buttons_list[i]
            const len = getByteLen(item)
            if (len > 12 || len < 4) {
              return callback(new Error(`第${i + 1}个标签${len}个字符，最少4个字符，最多12个字符，一个中文占两个字符`))
            }
          }
          callback()
        }
      }
    ],
    budget: [
      {
        required: true,
        validator(rule, value, callback) {
          const { budget } = planForm

          if (isSearch.value) {
            if (isDuration.value) {
              // 搜索广告极速智投，不需要设置预算，同项目预算
              return callback()
            }
          }

          if (adBudgetVisible.value) {
            if (!budget) return callback(new Error('请输入预算'))
            if (!twoDigit.test(budget)) return callback(new Error('最多保留2位小数'))
            if (budget > MAXIMUM_BUDGET) return callback(new Error(`预算不能高于${MAXIMUM_BUDGET}`))
            if (budget < MINIMUM_BUDGET) return callback(new Error(`预算不能低于${MINIMUM_BUDGET}`))
          }
          callback()
        }
      }
    ],
    cpa_bid: [
      {
        required: true,
        validator(rule, value, callback) {
          let { bid_type, cpa_bid, budget, bid_num_range } = planForm
          const { delivery_mode } = projectForm
          value = Number(cpa_bid)
          budget = Number(budget)

          // 搜索广告，不需要设置出价，同项目出价
          if (isSearch.value) callback()

          if (delivery_mode === DELIVERY_MODE.MANUAL && adBidVisible.value) {
            if (bid_type === BID_VALUE_TYPE.RANGE) {
              const [minBid, maxBid] = bid_num_range.map(Number)
              if (!minBid) return callback(new Error('请输入最低出价'))
              if (!maxBid) return callback(new Error('请输入最高出价'))
              if (minBid > maxBid) return callback(new Error('最高出价不能低于最低出价'))
              if (minBid < MINIMUM_PLAN_BID) return callback(new Error(`最低出价不能低于${MINIMUM_PLAN_BID}`))
              if (maxBid > MAXIMUM_PLAN_BID) return callback(new Error(`最高出价不能高于${MAXIMUM_PLAN_BID}`))
              if (!twoDigit.test(minBid)) return callback(new Error('最多保留2位小数'))
              if (!twoDigit.test(maxBid)) return callback(new Error('最多保留2位小数'))
              if (budget && maxBid > budget) return callback(new Error('出价须小于预算'))
            } else {
              if (!value) return callback(new Error('请输入出价'))
              if (!twoDigit.test(value)) return callback(new Error('最多保留2位小数'))
              if (value < MINIMUM_PLAN_BID) return callback(new Error(`出价不能低于${MINIMUM_PLAN_BID}`))
              if (value > MAXIMUM_PLAN_BID) return callback(new Error(`出价不能高于${MAXIMUM_PLAN_BID}`))
              if (value > budget) return callback(new Error('出价须小于预算'))
            }
          }

          callback()
        }
      }
    ],
    roi_goal: [
      {
        required: true,
        validator(rule, value, callback) {
          if (isSearch.value) return callback()

          const { delivery_mode } = projectForm
          const { roi_bid_type, roi_goal, roi_bid_num_range } = planForm
          value = Number(roi_goal)

          if (delivery_mode === DELIVERY_MODE.MANUAL && adBidVisible.value && adDeepBidVisible.value) {
            if (roi_bid_type === BID_VALUE_TYPE.RANGE) {
              const [minBid, maxBid] = roi_bid_num_range.map(Number)
              if (!minBid) return callback(new Error('请输入最低roi'))
              if (!maxBid) return callback(new Error('请输入最高roi'))
              if (minBid > maxBid) return callback(new Error('最高roi不能低于最低roi'))
              if (minBid < MINIMUM_ROI) return callback(new Error(`最低roi不能低于${MINIMUM_ROI}`))
              if (maxBid > MAXIMUM_ROI) return callback(new Error(`最高roi不能高于${MAXIMUM_ROI}`))
              if (!fourDigit.test(minBid)) return callback(new Error('最多保留4位小数'))
              if (!fourDigit.test(maxBid)) return callback(new Error('最多保留4位小数'))
            } else {
              if (value && !fourDigit.test(value)) return callback(new Error('最多保留4位小数'))
              if (value > MAXIMUM_ROI) return callback(new Error(`ROI目标不能高于${MAXIMUM_ROI}`))
              if (value < MINIMUM_ROI) return callback(new Error(`ROI目标不能低于${MINIMUM_ROI}`))
            }
          }
          callback()
        }
      }
    ]
  }
}

const HORIZONTAL_VALID = {
  WIDTH: 1280,
  HEIGHT: 720
}

const VERTICAL_VALID = {
  WIDTH: 720,
  HEIGHT: 1280
}

const LOGO_VALID = {
  WIDTH: 108,
  HEIGHT: 108
}

const SMALL_IMAGE_VALID = {
  WIDTH: 456,
  HEIGHT: 300
}

const EXT_VALID = ['mp4', 'mpeg', '3gp', 'avi']

/**
 * @description: 创意表单校验
 * @return {*}
 */
export function getCreativeFormRules() {
  const { creativeForm, projectForm } = this
  const { isDuration } = useOptimizeGoalRelative(projectForm)
  return {
    creative_name: [
      {
        required: true,
        validator(rule, value, callback) {
          if (!value) return callback(new Error('请填写创意名称'))
          if (Math.ceil(getByteLen(value)) > 90) return callback(new Error('创意名称长度超出限制'))
          callback()
        }
      }
    ],
    selectedMaterials: [
      {
        required: true,
        validator(rule, value, callback) {
          const { video, image, imageAudio } = value

          if (!video.length && !image.length && !imageAudio.length) {
            return callback(new Error('视频、图片、图文素材类型至少选择一种'))
          }

          if (isDuration.value) {
            if (Object.values(value).flat().length < 3) {
              return callback(new Error('搜索广告极速智投最少需要3个视频加图片素材'))
            }
          }
          if (video?.length) {
            let msg = ''
            for (let i = 0; i < video.length; i++) {
              const { width, height, size, cover_size, cover_height, cover_width } = video[i]
              const [ext] = video[i].url.split('.')?.slice(-1) || []

              const idx = i + 1

              if (ext) {
                if (!EXT_VALID.includes(ext.toLowerCase())) {
                  msg = `视频${idx}：视频格式不支持，支持格式：${EXT_VALID.join('、')}，当前格式：${ext}`
                  break
                }
              }

              if (size / 1024 / 1024 > 1000) {
                msg = `视频${idx}：视频大小不能超过1000MB`
                break
              }

              if (cover_size / 1024 > 500) {
                msg = `视频${idx}：视频封面大小不能超过500KB`
                break
              }

              // 横版视频
              if (width > height) {
                if (width < HORIZONTAL_VALID.WIDTH && height < HORIZONTAL_VALID.HEIGHT) {
                  msg = `视频${idx}：横版视频最低尺寸${HORIZONTAL_VALID.WIDTH}*${HORIZONTAL_VALID.HEIGHT}`
                  break
                }
                if (+(width / height).toFixed(2) !== 1.78) {
                  msg = `视频${idx}：横版视频比例应为16:9，当前尺寸${width}*${height}`
                  break
                }

                // 校验智能封面尺寸与视频尺寸是否一致
                if (cover_size) {
                  if (+(cover_width / cover_height).toFixed(2) !== 1.78) {
                    msg = `视频${idx}：横版视频封面比例应为16:9，当前尺寸${cover_width}*${cover_height}`
                    break
                  }
                }
              }

              // 竖版视频
              if (height > width) {
                if (height < VERTICAL_VALID.HEIGHT && width < VERTICAL_VALID.WIDTH) {
                  msg = `视频${idx}：竖版视频最低尺寸${VERTICAL_VALID.WIDTH}*${VERTICAL_VALID.HEIGHT}`
                  break
                }
                if (+(height / width).toFixed(2) !== 1.78) {
                  msg = `视频${idx}：竖版视频比例应为9:16，当前尺寸${width}*${height}`
                  break
                }

                // 校验智能封面尺寸与视频尺寸是否一致
                if (cover_size) {
                  if (+(cover_height / cover_width).toFixed(2) !== 1.78) {
                    msg = `视频${idx}：竖版视频封面比例应为9:16，当前尺寸${cover_width}*${cover_width}`
                    break
                  }
                }
              }
            }

            if (msg) return callback(new Error(msg))
          }

          if (image?.length) {
            let msg = ''
            for (let i = 0; i < image.length; i++) {
              const { width, height, size } = image[i]

              const idx = i + 1
              if (size / 1024 > 500) {
                msg = `图片${idx}：图片大小不能超过500KB`
                break
              }

              // 大图横版、小图横版
              if (width >= height) {
                const ratio = +(width / height).toFixed(2)
                if (ratio !== 1.78 && ratio !== 1.52) {
                  msg = `图片${idx}：横版大图宽高比例应为1.78，小图宽高比例应为1.52 ，当前尺寸${width}*${height}，当前宽高比为${ratio}`
                  break
                }

                if (ratio === 1.52 && width < SMALL_IMAGE_VALID.WIDTH && height < SMALL_IMAGE_VALID.HEIGHT) {
                  msg = `图片${idx}：小图最低尺寸${SMALL_IMAGE_VALID.WIDTH}*${SMALL_IMAGE_VALID.HEIGHT}`
                  break
                }

                if (ratio === 1.78 && width < HORIZONTAL_VALID.WIDTH && height < HORIZONTAL_VALID.HEIGHT) {
                  msg = `图片${idx}：横版图片最低尺寸${HORIZONTAL_VALID.WIDTH}*${HORIZONTAL_VALID.HEIGHT}`
                  break
                }
              }

              // 大图竖版
              if (height > width) {
                const ratio = +(height / width).toFixed(2)
                if (ratio !== 1.78) {
                  msg = `图片${idx}：竖版大图宽高比例应为1.78，当前尺寸${height}*${width}，当前宽高比为${ratio}`
                }

                if (width < VERTICAL_VALID.WIDTH && height < VERTICAL_VALID.HEIGHT) {
                  msg = `图片${idx}：竖版图片最低尺寸${VERTICAL_VALID.WIDTH}*${VERTICAL_VALID.HEIGHT}`
                  break
                }
              }
            }

            if (msg) return callback(new Error(msg))
          }

          callback()
        }
      }
    ]
  }
}
